import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { useAuth } from "./AuthContext";
import UpgradeModal from "../components/UpgradeModal";
import { api } from "../lib/api";

/**
 * PlanContext v2 — 3-tier model (free / premium / executive).
 * Exposes plan, limits, catalog, promotion, showUpgrade(reasonKey), requireFeature(key).
 */
const PlanContext = createContext(null);
export const usePlan = () => useContext(PlanContext) || {};

const REASON_COPY = {
  scans_per_day: {
    title: "You've hit today's scan limit",
    body: "Free accounts get 15 scans per day. Upgrade to Premium for unlimited scans, real-time data and the full indicator library.",
    suggested: "premium",
  },
  watchlists: {
    title: "Free plan is capped at 1 watchlist",
    body: "Upgrade to Premium and create unlimited watchlists — perfect for tracking multiple sectors, strategies or timeframes side by side.",
    suggested: "premium",
  },
  watchlist_limit: {
    title: "Watchlist limit reached",
    body: "Upgrade for unlimited watchlists.",
    suggested: "premium",
  },
  symbols_per_watchlist: {
    title: "Watchlist symbol cap reached",
    body: "Your plan's symbol cap has been reached. Upgrade for more headroom per watchlist.",
    suggested: "premium",
  },
  symbol_limit: {
    title: "Watchlist symbol cap reached",
    body: "Your plan's symbol cap has been reached. Upgrade for more headroom per watchlist.",
    suggested: "premium",
  },
  watchlist_stability: {
    title: "Maximum watchlist size reached",
    body: "You've reached the maximum watchlist size for performance stability. Create another watchlist to continue.",
    suggested: "executive",
  },
  alerts: {
    title: "You've used all 3 alerts",
    body: "Free accounts get 3 active alerts. Upgrade to Premium for unlimited alerts, SMS delivery, custom filters and AI insights.",
    suggested: "premium",
  },
  alert_limit: {
    title: "Alert limit reached",
    body: "Upgrade to Premium for unlimited alerts and SMS delivery.",
    suggested: "premium",
  },
  sms_alerts: {
    title: "SMS Alerts are a Premium feature",
    body: "Deliver alerts straight to your phone via SMS — Premium and Executive only.",
    suggested: "premium",
  },
  sms_gated: {
    title: "SMS Alerts are a Premium feature",
    body: "Deliver alerts straight to your phone via SMS — Premium and Executive only.",
    suggested: "premium",
  },
  premium_filters: {
    title: "Premium filter",
    body: "Custom filters are a Premium feature. Upgrade for the full filter set, saved presets and priority scanner speed.",
    suggested: "premium",
  },
  custom_filters: {
    title: "Custom Filters are Premium",
    body: "Design your own custom filters — Premium and Executive only.",
    suggested: "premium",
  },
  ai_assistant: {
    title: "AI Trading Assistant is Premium",
    body: "Ask the AI Assistant about setups, indicators and risk — Premium and Executive only.",
    suggested: "premium",
  },
  ai_gated: {
    title: "AI Trading Assistant is Premium",
    body: "Ask the AI Assistant about setups, indicators and risk — Premium and Executive only.",
    suggested: "premium",
  },
  real_time_data: {
    title: "Real-time market data is Premium",
    body: "Free accounts see delayed/demo prices. Upgrade to Premium for real-time WebSocket ticks.",
    suggested: "premium",
  },
  backtester_gated: {
    title: "Backtesting is an Executive Feature.",
    body: "Design, backtest and compare strategies with unlimited historical data — Executive only.",
    suggested: "executive",
  },
  share_gated: {
    title: "Sharing scans is Executive-only",
    body: "Post your exact scan setup + top hits straight to the community with one click — Executive only. Great way to build a following and get feedback on your setups.",
    suggested: "executive",
  },
  fork_gated: {
    title: "Forking scans is Executive-only",
    body: "One-click copy any shared scan setup straight into your own scanner — Executive only. The original author gets +2 karma when you fork.",
    suggested: "executive",
  },
  strategy_builder: {
    title: "Strategy Builder is Executive",
    body: "Design multi-condition strategies with unlimited historical backtesting — Executive only.",
    suggested: "executive",
  },
  exports: {
    title: "Exports are an Executive feature",
    body: "Export backtests, watchlists and results as CSV, Excel or PDF — Executive only.",
    suggested: "executive",
  },
  trade_replay: {
    title: "Trade Replay is Executive",
    body: "Replay any historical setup tick-by-tick — Executive only.",
    suggested: "executive",
  },
  default: {
    title: "Unlock More with FlowFinder",
    body: "Upgrade for real-time data, unlimited watchlists & alerts, AI Trading Assistant, and institutional-grade backtesting.",
    suggested: "premium",
  },
};

export function PlanProvider({ children }) {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [promotion, setPromotion] = useState({ active: false });
  const [reason, setReason] = useState(null);

  useEffect(() => {
    api.get("/api/plans")
      .then(({ data }) => {
        setCatalog(data.plans || []);
        setPromotion(data.promotion || { active: false });
      })
      .catch(() => {});
  }, []);

  const plan = user?.plan || "free";
  const limits = user?.limits || {};
  const isTrial = !!user?.trial_active;
  const trialSecondsLeft = user?.trial_seconds_left || 0;
  const refCode = user?.referral_code || null;

  const showUpgrade = useCallback((reasonKey = "default") => {
    setReason(REASON_COPY[reasonKey] || REASON_COPY.default);
  }, []);

  const requireFeature = useCallback((featureKey) => {
    const v = limits[featureKey];
    if (v === true || v === null || v === "all" || Array.isArray(v)) return true;
    if (v === false || v === undefined) {
      setReason(REASON_COPY[featureKey] || REASON_COPY.default);
      return false;
    }
    return true;
  }, [limits]);

  const value = useMemo(() => ({
    plan, limits, isTrial, trialSecondsLeft, refCode,
    catalog, promotion, showUpgrade, requireFeature,
  }), [plan, limits, isTrial, trialSecondsLeft, refCode, catalog, promotion, showUpgrade, requireFeature]);

  return (
    <PlanContext.Provider value={value}>
      {children}
      <UpgradeModal open={!!reason} reason={reason} onClose={() => setReason(null)} />
    </PlanContext.Provider>
  );
}
